/**
 * WB3-P2-2 — Intent namespace union (variant-scoped)
 * @see doc/system-completion-plan/01-target/BUSINESS_WORKBENCH_NORTH_STAR_V3.md §4
 */

import type { UserRole } from '@shared/types/roles';
import type { WorkbenchAction, WorkbenchActionGroup } from './action-types';
import { resolveRoleActionBoost } from './workbench-role-cockpit';

export type OperationalIntent =
  | 'general_browse'
  | 'collect_payment'
  | 'process_new_order'
  | 'resolve_claim'
  | 'approve_credit'
  | 'prepare_delivery'
  | 'receive_inventory'
  | 'complete_task'
  | 'audit_review'
  | 'investigate_exception';

/** Site variant — Site Studio / public surface */
export type SiteIntent =
  | 'edit_site_page'
  | 'configure_site_navigation'
  | 'bind_site_action'
  | 'publish_site'
  | 'review_site_access'
  | 'configure_portal_surface';

/** Document variant — EDS · OrgDocument · print forms */
export type DocumentIntent =
  | 'edit_template'
  | 'map_fields'
  | 'preview_document'
  | 'approve_revision'
  | 'publish_template'
  | 'print_document'
  | 'extract_document_data'
  | 'audit_document_change';

/** Internal Entity permission / access administration */
export type AccessIntent =
  | 'review_permissions'
  | 'edit_permission_matrix'
  | 'assign_role'
  | 'audit_access'
  | 'compare_role_preset'
  | 'approve_access_change'
  | 'investigate_permission_issue';

/** External Entity relationship · portal · counterparty policy */
export type RelationshipIntent =
  | 'onboard_external_entity'
  | 'review_relationship'
  | 'manage_contract'
  | 'configure_portal_access'
  | 'review_documents'
  | 'audit_external_activity';

/** Union — variant-scoped intent namespaces */
export type WorkbenchIntent =
  | OperationalIntent
  | SiteIntent
  | DocumentIntent
  | AccessIntent
  | RelationshipIntent;

export type IntentNamespace = 'operational' | 'site' | 'document' | 'access' | 'relationship';

export const OPERATIONAL_INTENTS = [
  'general_browse',
  'collect_payment',
  'process_new_order',
  'resolve_claim',
  'approve_credit',
  'prepare_delivery',
  'receive_inventory',
  'complete_task',
  'audit_review',
  'investigate_exception',
] as const satisfies readonly OperationalIntent[];

export const SITE_INTENTS = [
  'edit_site_page',
  'configure_site_navigation',
  'bind_site_action',
  'publish_site',
  'review_site_access',
  'configure_portal_surface',
] as const satisfies readonly SiteIntent[];

export const DOCUMENT_INTENTS = [
  'edit_template',
  'map_fields',
  'preview_document',
  'approve_revision',
  'publish_template',
  'print_document',
  'extract_document_data',
  'audit_document_change',
] as const satisfies readonly DocumentIntent[];

export const ACCESS_INTENTS = [
  'review_permissions',
  'edit_permission_matrix',
  'assign_role',
  'audit_access',
  'compare_role_preset',
  'approve_access_change',
  'investigate_permission_issue',
] as const satisfies readonly AccessIntent[];

export const RELATIONSHIP_INTENTS = [
  'onboard_external_entity',
  'review_relationship',
  'manage_contract',
  'configure_portal_access',
  'review_documents',
  'audit_external_activity',
] as const satisfies readonly RelationshipIntent[];

export const DEFAULT_OPERATIONAL_INTENT: OperationalIntent = 'general_browse';

const OPERATIONAL_INTENT_SET = new Set<string>(OPERATIONAL_INTENTS);
const SITE_INTENT_SET = new Set<string>(SITE_INTENTS);
const DOCUMENT_INTENT_SET = new Set<string>(DOCUMENT_INTENTS);
const ACCESS_INTENT_SET = new Set<string>(ACCESS_INTENTS);
const RELATIONSHIP_INTENT_SET = new Set<string>(RELATIONSHIP_INTENTS);

export function isOperationalIntent(intent: WorkbenchIntent): intent is OperationalIntent {
  return OPERATIONAL_INTENT_SET.has(intent);
}

export function isSiteIntent(intent: WorkbenchIntent): intent is SiteIntent {
  return SITE_INTENT_SET.has(intent);
}

export function isDocumentIntent(intent: WorkbenchIntent): intent is DocumentIntent {
  return DOCUMENT_INTENT_SET.has(intent);
}

export function isAccessIntent(intent: WorkbenchIntent): intent is AccessIntent {
  return ACCESS_INTENT_SET.has(intent);
}

export function isRelationshipIntent(intent: WorkbenchIntent): intent is RelationshipIntent {
  return RELATIONSHIP_INTENT_SET.has(intent);
}

export function getIntentNamespace(intent: WorkbenchIntent): IntentNamespace {
  if (isSiteIntent(intent)) {
    return 'site';
  }
  if (isDocumentIntent(intent)) {
    return 'document';
  }
  if (isAccessIntent(intent)) {
    return 'access';
  }
  if (isRelationshipIntent(intent)) {
    return 'relationship';
  }
  return 'operational';
}

const NAMESPACE_ACTION_GROUPS: Readonly<Record<IntentNamespace, readonly WorkbenchActionGroup[]>> =
  {
    operational: ['open', 'create', 'state_change', 'assign', 'attach', 'export', 'approve'],
    site: ['admin', 'state_change', 'open'],
    document: ['print', 'export', 'approve', 'open', 'attach'],
    access: ['admin', 'approve', 'open'],
    relationship: ['assign', 'state_change', 'attach', 'open', 'approve'],
  };

const INTENT_ACTION_ID_BOOST: Readonly<
  Partial<Record<WorkbenchIntent, Readonly<Record<string, number>>>>
> = {
  collect_payment: { export: 15, 'finance:collect': 25 },
  process_new_order: { 'order:create': 20, create: 15 },
  receive_inventory: { 'inventory:receive': 20 },
  prepare_delivery: { 'delivery:dispatch': 20 },
  complete_task: { 'task:complete': 25 },
  investigate_exception: { view: 10 },
  edit_template: { edit: 20, 'template:edit': 25 },
  preview_document: { view: 20, print: 15 },
  print_document: { print: 30 },
  review_permissions: { view: 15, edit: 10 },
  edit_permission_matrix: { edit: 25 },
  onboard_external_entity: { create: 20 },
  configure_portal_access: { edit: 20, admin: 15 },
  review_relationship: { view: 20 },
};

function resolveActionBoost(
  action: WorkbenchAction,
  intent: WorkbenchIntent,
  role?: UserRole
): number {
  const namespace = getIntentNamespace(intent);
  const groups = NAMESPACE_ACTION_GROUPS[namespace];
  let boost = 0;

  if (action.group !== undefined && groups.includes(action.group)) {
    boost += 10;
  }

  const idBoosts = INTENT_ACTION_ID_BOOST[intent];
  if (idBoosts !== undefined) {
    for (const [fragment, value] of Object.entries(idBoosts)) {
      if (action.id.includes(fragment) || action.handlerRef.includes(fragment)) {
        boost += value;
      }
    }
  }

  if (role !== undefined) {
    boost += resolveRoleActionBoost(role, action);
  }

  return boost;
}

/** Namespace-aware filter — operational intents pass all actions; others deprioritize non-affinity rows. */
export function filterActionsByIntent<T extends WorkbenchAction>(
  actions: readonly T[],
  intent: WorkbenchIntent | undefined
): T[] {
  if (intent === undefined || isOperationalIntent(intent)) {
    return [...actions];
  }

  const namespace = getIntentNamespace(intent);
  const groups = NAMESPACE_ACTION_GROUPS[namespace];

  return actions.filter((action) => {
    if (action.group === undefined) {
      return true;
    }
    return groups.includes(action.group);
  });
}

/** Re-ranks actions by base priority + intent affinity boost (WB3 §5.6 applyIntentPriority). */
export function applyIntentPriority<T extends WorkbenchAction>(
  actions: readonly T[],
  intent: WorkbenchIntent | undefined,
  role?: UserRole
): T[] {
  if (intent === undefined) {
    return [...actions];
  }

  return [...actions]
    .map((action) => ({
      action,
      effectivePriority: action.priority + resolveActionBoost(action, intent, role),
    }))
    .sort((a, b) => b.effectivePriority - a.effectivePriority)
    .map(({ action }) => action);
}

/** Filter then priority — kernel pipeline helper. */
export function resolveIntentActionOrdering<T extends WorkbenchAction>(
  actions: readonly T[],
  intent: WorkbenchIntent | undefined,
  role?: UserRole
): T[] {
  return applyIntentPriority(filterActionsByIntent(actions, intent), intent, role);
}
